/**
 * SIMS-8783 — the Analytes tab's pure list behaviour.
 *
 * Imports are RELATIVE, not `@/` — see the note in index.tsx: the alias is honoured by tsc and by
 * jest, but CRA's webpack ignores tsconfig paths, so it only works for imports that resolve to
 * types and get erased before webpack sees them.
 */
import { IAnalyte } from "../../domain/analyte.interface";
import { CupResponse } from "../../domain/cup.interface";
import { cupAnalytes } from "./cupsList";

/** AC3 — the two fields an analyte can be found by. */
export const searchableAnalyteFields = (
	analyte: IAnalyte,
): (string | undefined | null)[] => [analyte.shortname, analyte.scientificName];

/** AC4 — the single filter dimension for the Analytes tab. */
export const analyteTypeCategories = (analyte: IAnalyte): string[] =>
	analyte.type ? [String(analyte.type)] : [];

/**
 * AC4 — the type chips, DERIVED from the data.
 *
 * The Hi-Fi declares `['Stimulant','Other substance']` as a literal (markup 1007-1009) while
 * deriving the vendor chips from the data. AC4 asks for both to behave "in the same way", and the
 * legacy set in that same block carries five types — a hard-coded pair would leave any analyte
 * outside the two with no chip to find it by. Question Q2 on the ticket.
 */
export const typeFilterOptions = (analytes: IAnalyte[]): string[] =>
	Array.from(
		new Set(analytes.map((a) => String(a.type ?? "")).filter(Boolean)),
	).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

/** Shared with the Cups tab so an id-shaped payload counts the same on both. */
const analytesOf = (cup: CupResponse) => cupAnalytes(cup);

/**
 * How many cups carry this analyte — the "In cups" column.
 *
 * Derived client-side: `GET /analytes` returns no usage figure and SIMS-8962 added none. Matched by
 * `_id`, falling back to the shortname for payloads that carry the code but not the id.
 *
 * This is a COUNT and never names the cups. Naming them is SIMS-8814 AC3, in the analyte editor —
 * the epic's deliberate split, and the reason Q5 is a wording question rather than a missing
 * feature.
 */
export const cupCarries = (cup: CupResponse, analyte: IAnalyte): boolean =>
	analytesOf(cup).some((a) =>
		// BOTH ids must be present to compare them. Guarding only the cup-side id meant that
		// a catalogue analyte with no `_id` compared `a._id === undefined`, which is false,
		// so the shortname fallback never fired and the count silently read 0.
		a._id && analyte._id
			? a._id === analyte._id
			: a.shortname === analyte.shortname,
	);

export const cupsCarrying = (analyte: IAnalyte, cups: CupResponse[]): number =>
	cups.filter((cup) => cupCarries(cup, analyte)).length;

/**
 * SIMS-8814 AC3 — the same membership rule, returning the CUPS rather than a count.
 *
 * Shares `cupCarries` with the count on purpose: two copies of the matching rule would let
 * the Analytes tab report "3 cups" while the editor listed two of them.
 */
export const cupsCarryingList = (
	analyte: IAnalyte,
	cups: CupResponse[],
): CupResponse[] => cups.filter((cup) => cupCarries(cup, analyte));

/**
 * The same count for every analyte, computed in ONE pass over the cups.
 *
 * `cupsCarrying` per row is O(cups x analytes-per-cup); used as a sort comparator that becomes
 * O(n log n x m) on every render. The column and the sort both read this map instead.
 */
export const buildUsageMap = (
	analytes: IAnalyte[],
	cups: CupResponse[],
): Map<string, number> => {
	const counts = new Map<string, number>();
	for (const analyte of analytes) {
		counts.set(analyte._id, cupsCarrying(analyte, cups));
	}
	return counts;
};

/** Sort keys the Analytes table offers. */
export const analyteSortValue = (
	analyte: IAnalyte,
	key: string,
	cups: CupResponse[],
	usage?: Map<string, number>,
): unknown => {
	switch (key) {
		case "scientificName":
			return analyte.scientificName;
		case "type":
			return String(analyte.type ?? "");
		case "status":
			return analyte.status;
		case "inCups":
			return usage?.get(analyte._id) ?? cupsCarrying(analyte, cups);
		case "shortname":
		default:
			return analyte.shortname;
	}
};

/** The list states, with copy that avoids "panel" for the same reason the cups copy does. */
export const analytesStateCopy = {
	loadFailedTitle: "Analytes couldn't be loaded",
	loadFailedBody:
		"The request failed before anything was returned. Nothing has been changed.",
	emptyTitle: "No analytes yet",
	emptyBody:
		"Analytes are the substances a cup detects. Add the first analyte, then build a cup from it.",
	emptyIcon: "science",
	loadingLabel: "Loading analytes…",
	noResultsTitle: (query: string) => `No analytes match “${query}”`,
	noResultsBody:
		"No shortname or scientific name contains that text with the current type filter.",
	addLabel: "Create analyte",
};
